import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import FormInput from "../components/FormInput";
import FormSelect from "../components/FormSelect";
import PrimaryButton from "../components/PrimaryButton";

import bundledPrintLogo from "../assets/alkhidmat-print-logo-new.png";
import bundledFallbackLogo from "../assets/alkhidmat-logo.png";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";
const TANNERY_DEFAULT_EID_DAY_STORAGE_KEY = "tanneryDefaultEidDay";
const EID_DAY_OPTIONS = ["Eid Day 1", "Eid Day 2", "Eid Day 3", "Eid Day 4"];

const extractEidDayFromSettingsPayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  const raw =
    payload.data?.eid_day ??
    payload.eid_day ??
    payload.default_eid_day ??
    "";
  return String(raw ?? "").trim();
};

const parseMaxEidDayNumberFromLabel = (value) => {
  const s = String(value ?? "").trim();
  if (!s) {
    return null;
  }
  const directIdx = EID_DAY_OPTIONS.findIndex((label) => label.toLowerCase() === s.toLowerCase());
  if (directIdx >= 0) {
    return directIdx + 1;
  }
  const match = s.match(/(\d+)/);
  if (match) {
    const n = parseInt(match[1], 10);
    if (n >= 1 && n <= EID_DAY_OPTIONS.length) {
      return n;
    }
  }
  return null;
};

/** Submit Quantity: Eid Day 1 .. N where N is the tannery admin default (inclusive). */
const buildSubmitEidDayOptions = (maxDayLabel) => {
  const trimmed = String(maxDayLabel ?? "").trim();
  if (!trimmed) {
    return [...EID_DAY_OPTIONS];
  }
  const n = parseMaxEidDayNumberFromLabel(trimmed);
  if (n === null) {
    return [...EID_DAY_OPTIONS];
  }
  return EID_DAY_OPTIONS.slice(0, n);
};
const LIVESTOCK_FIELDS = [
  { key: "cow_count", label: "Cow" },
  { key: "goat_count", label: "Goat" },
  { key: "sheep_count", label: "Sheep" },
  { key: "camel_count", label: "Camel" },
  { key: "buffalo_count", label: "Buffalo" },
];
const LIVESTOCK_FIELD_KEYS = new Set(LIVESTOCK_FIELDS.map((field) => field.key));

const sumLivestockCounts = (counts) =>
  LIVESTOCK_FIELDS.reduce(
    (sum, field) => sum + Math.max(0, parseInt(String(counts?.[field.key] ?? "0"), 10) || 0),
    0
  );
const CHECKOUT_FILTER_OPTIONS = [
  { label: "Waiting", value: "waiting" },
  { label: "Checked Out", value: "checked_out" },
  { label: "All", value: "all" },
];
const PRINT_IMAGE_WAIT_TIMEOUT_MS = 400;
const PRINT_LOGO_MAX_WAIT_MS = 1000;
/** Max pixel width when inlining logos for print (display CSS is smaller). */
const PRINT_LOGO_MAX_WIDTH_PX = 200;
const PRINT_FIFTY_YEARS_MAX_WIDTH_PX = 120;

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const getFriendlyErrorMessage = (statusCode, payload, fallbackMessage) => {
  if (statusCode === 401) {
    return "Your session has expired. Please login again.";
  }
  if (statusCode === 403) {
    return "You are not allowed to update this token.";
  }
  if (statusCode === 400 && typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }
  if (statusCode === 404) {
    return "The selected token was not found.";
  }

  if (statusCode === 422 && Array.isArray(payload?.detail)) {
    const validationMessages = payload.detail
      .map((detailItem) => {
        const fieldName = Array.isArray(detailItem?.loc)
          ? detailItem.loc[detailItem.loc.length - 1]
          : "";
        const prettyFieldName =
          typeof fieldName === "string"
            ? fieldName.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
            : "";
        return prettyFieldName
          ? `${prettyFieldName}: ${detailItem?.msg || "Invalid value."}`
          : detailItem?.msg || "";
      })
      .filter(Boolean);

    if (validationMessages.length > 0) {
      return validationMessages.join(" ");
    }

    return "Please review the quantity values, Eid day, and comment.";
  }

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message;
  }

  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }

  return fallbackMessage;
};

const escapeHtml = (unsafeValue) =>
  String(unsafeValue ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const getPrintFriendlyErrorMessage = (statusCode, payload, fallbackMessage) => {
  if (statusCode === 401) {
    return "Your session has expired. Please login again.";
  }
  if (statusCode === 403) {
    return "Printing is only allowed for data entry or tannery admin on tokens from your tannery.";
  }
  if (statusCode === 404) {
    return "The selected token was not found.";
  }
  if (statusCode === 400) {
    if (typeof payload?.detail === "string" && payload.detail.trim()) {
      return payload.detail;
    }
    if (typeof payload?.message === "string" && payload.message.trim()) {
      return payload.message;
    }
    return "This token cannot be printed until checkout is completed.";
  }
  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }
  return fallbackMessage;
};

const hasSubmittedLivestock = (token) => {
  if (!token || typeof token !== "object") {
    return false;
  }

  if (typeof token.checked_out === "boolean") {
    return token.checked_out;
  }
  if (typeof token.is_checked_out === "boolean") {
    return token.is_checked_out;
  }

  const normalizedStatus = String(token.status || "").trim().toUpperCase();
  if (["CHECKED_OUT", "CHECKEDOUT", "CHECKED OUT", "COMPLETED"].includes(normalizedStatus)) {
    return true;
  }

  const countTotal =
    Number(token.cow_count || 0) +
    Number(token.goat_count || 0) +
    Number(token.sheep_count || 0) +
    Number(token.camel_count || 0) +
    Number(token.buffalo_count || 0);

  return Boolean(token.eid_day) && countTotal > 0;
};

const hasTokenBeenEdited = (token) => {
  if (!token || typeof token !== "object") {
    return false;
  }

  const updateCheck = String(token.update_check || "").trim().toUpperCase();
  return updateCheck === "UPDATED";
};

const parseHistoryTimestampMs = (value) => {
  const t = Date.parse(String(value ?? "").trim());
  return Number.isFinite(t) ? t : 0;
};

const compareLivestockHistoryRows = (a, b) => {
  const diff = parseHistoryTimestampMs(a?.updated_at) - parseHistoryTimestampMs(b?.updated_at);
  if (diff !== 0) {
    return diff;
  }
  const ida = a?.id;
  const idb = b?.id;
  const sa = ida === null || ida === undefined ? "" : String(ida);
  const sb = idb === null || idb === undefined ? "" : String(idb);
  const na = Number(sa);
  const nb = Number(sb);
  if (Number.isFinite(na) && Number.isFinite(nb) && String(na) === sa && String(nb) === sb) {
    return na - nb;
  }
  return sa.localeCompare(sb);
};

const normalizeLivestockHistoryList = (rawList) => {
  if (!Array.isArray(rawList)) {
    return [];
  }
  const sorted = [...rawList].sort(compareLivestockHistoryRows);
  return sorted.map((item, index) => {
    if (item && typeof item === "object" && typeof item.is_initial_entry === "boolean") {
      return item;
    }
    const base = item && typeof item === "object" ? item : {};
    return { ...base, is_initial_entry: index === 0 };
  });
};

const normalizeElaqaForForm = (value) => {
  const s = String(value ?? "").trim();
  if (!s || s === "-") {
    return "";
  }
  return s;
};

const isCentralOfficeZila = (zila) => {
  const s = String(zila ?? "").trim().toLowerCase();
  return s === "central office" || s === "central";
};

const normalizeZilaNameKey = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const extractElaqaNamesFromList = (list) => {
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .map((item) =>
      typeof item === "string"
        ? item.trim()
        : String(item?.elaqa_name ?? item?.name ?? item?.elaqa ?? "").trim()
    )
    .filter(Boolean);
};

/** Supports flat arrays and nested API shapes (`data.elaqas`, etc.). */
const extractElaqaNamesFromApiPayload = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return extractElaqaNamesFromList(payload);
  }

  const direct = extractElaqaNamesFromList(payload.elaqas ?? payload.items ?? payload.results);
  if (direct.length > 0) {
    return direct;
  }

  const data = payload.data;
  if (Array.isArray(data)) {
    return extractElaqaNamesFromList(data);
  }
  if (data && typeof data === "object") {
    const nested = extractElaqaNamesFromList(
      data.elaqas ?? data.items ?? data.results ?? data.elaqa_list
    );
    if (nested.length > 0) {
      return nested;
    }
  }

  return [];
};

const extractZilaRecordsFromApiPayload = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload.filter((item) => item && typeof item === "object");
  }
  if (Array.isArray(payload?.data)) {
    return payload.data.filter((item) => item && typeof item === "object");
  }
  if (payload?.data && typeof payload.data === "object" && Array.isArray(payload.data.zilas)) {
    return payload.data.zilas.filter((item) => item && typeof item === "object");
  }
  if (Array.isArray(payload?.zilas)) {
    return payload.zilas.filter((item) => item && typeof item === "object");
  }
  return [];
};

const isZilaNameOnlyListPayload = (payload) => {
  const raw = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  return raw.length > 0 && raw.every((item) => typeof item === "string");
};

const getZilaRecordName = (record) =>
  String(record?.name ?? record?.zila_name ?? record?.zila ?? "").trim();

const getZilaRecordId = (record) => {
  const raw = record?.id ?? record?.zila_id ?? record?.zilaId;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const extractElaqasFromZilaRecord = (record) => {
  if (!record || typeof record !== "object") {
    return [];
  }
  return extractElaqaNamesFromList(
    record.elaqas ?? record.elaqa_list ?? record.elaqaList ?? record.items
  );
};

const findZilaRecord = (zilaRecords, zilaName, zilaId) => {
  const nameKey = normalizeZilaNameKey(zilaName);
  const parsedId = Number(zilaId);
  const hasId = Number.isInteger(parsedId) && parsedId > 0;

  return zilaRecords.find((record) => {
    if (hasId && getZilaRecordId(record) === parsedId) {
      return true;
    }
    if (!nameKey) {
      return false;
    }
    return normalizeZilaNameKey(getZilaRecordName(record)) === nameKey;
  });
};

const buildDistrictZilaElaqasUrl = (zilaName, zilaId) => {
  const parsedId = Number(zilaId);
  if (Number.isInteger(parsedId) && parsedId > 0) {
    return `${getBaseApiUrl()}/districts/zilas/${parsedId}/elaqas`;
  }
  return `${getBaseApiUrl()}/districts/zilas/${encodeURIComponent(zilaName)}/elaqas`;
};

const normalizeTokenSearchQuery = (value) => String(value ?? "").trim();

const normalizeToken = (token) => ({
  id: token.id || token.token_id || "",
  short_code: token.short_code || "-",
  tannery_name: token.tannery_name || "-",
  name: token.name || "-",
  phone_number: token.phone_number || "-",
  vehicle_type: token.vehicle_type || "-",
  car_plate: token.car_plate || "-",
  elaqa: token.elaqa || "-",
  zila: token.zila || "-",
  zila_id: token.zila_id ?? token.zilaId ?? "",
  status: token.status || "-",
  entry_time: token.entry_time || "",
  token_generated_at: token.token_generated_time || token.token_generated_at || token.entry_time || "",
  data_entered_at:
    token.data_entered_time || token.data_enetered_at || token.data_entered_at || "",
  updated_at: token.updated_at || "",
  updated_by_username: token.updated_by_username || "-",
  updated_by_name: token.updated_by_name || "-",
  token_generated_by_username: token.token_generated_by_username || "",
  token_generated_by_name: token.token_generated_by_name || "",
  data_entered_by_username: token.data_entered_by_username || "",
  data_entered_by_name: token.data_entered_by_name || "",
  update_check: String(token.update_check || "NotUpdated").trim(),
  eid_day: token.eid_day || "",
  comment: token.comment || "",
  cow_count: Number(token.cow_count || 0),
  goat_count: Number(token.goat_count || 0),
  sheep_count: Number(token.sheep_count || 0),
  camel_count: Number(token.camel_count || 0),
  buffalo_count: Number(token.buffalo_count || 0),
  checked_out: hasSubmittedLivestock(token),
  is_updated: hasTokenBeenEdited(token),
});

const formatUserDisplay = (username, name) => {
  const safeUsername = String(username || "").trim();
  const safeName = String(name || "").trim();
  if (safeUsername && safeName) {
    return `${safeName} (${safeUsername})`;
  }
  if (safeName) {
    return safeName;
  }
  if (safeUsername) {
    return safeUsername;
  }
  return "-";
};

const unwrapBundledAsset = (imported) =>
  imported && typeof imported === "object" && imported.default !== undefined ? imported.default : imported;

const getPublicAssetBaseUrl = () => {
  if (typeof window === "undefined") {
    return "";
  }
  const publicUrl = process.env.PUBLIC_URL || "";
  const normalizedPublic = publicUrl === "/" ? "" : String(publicUrl).replace(/\/+$/, "");
  return `${window.location.origin}${normalizedPublic}`;
};

const resolveSameOriginUrl = (importedAssetPath) => {
  if (typeof window === "undefined" || importedAssetPath === null || importedAssetPath === undefined) {
    return "";
  }
  const path = String(unwrapBundledAsset(importedAssetPath)).trim();
  if (!path || path === "undefined" || path === "null") {
    return "";
  }
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
};

const binaryToBase64Chunked = (bytes) => {
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    const sub = bytes.subarray(i, i + chunk);
    binary += String.fromCharCode.apply(null, sub);
  }
  return btoa(binary);
};

/** Build data URL bytes; MIME forced when server sends octet-stream or legacy JFIF. */
const arrayBufferToDataUrl = (buffer, mime) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  return `data:${mime};base64,${binaryToBase64Chunked(bytes)}`;
};

const resizeImageBufferToPrintDataUrl = async (buffer, mime, maxWidth) => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !maxWidth ||
    maxWidth < 1
  ) {
    return arrayBufferToDataUrl(buffer, mime);
  }

  try {
    const blob = new Blob([buffer], { type: mime || "image/png" });
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, maxWidth / Math.max(bitmap.width, 1));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) {
      bitmap.close?.();
      return arrayBufferToDataUrl(buffer, mime);
    }
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    // PNG keeps transparency; JPEG turned transparent areas black on the receipt.
    return canvas.toDataURL("image/png");
  } catch {
    return arrayBufferToDataUrl(buffer, mime);
  }
};

const fetchLogoUrlToPrintDataUrl = async (absoluteUrl, forcedMimeFallback, maxWidth) => {
  if (!absoluteUrl) {
    return "";
  }
  const response = await fetch(absoluteUrl, { credentials: "same-origin", cache: "force-cache" });
  if (!response.ok) {
    return "";
  }
  const buf = await response.arrayBuffer();
  let mime = response.headers.get("content-type") || "";
  mime = mime.split(";")[0].trim().toLowerCase();
  if (
    !mime ||
    mime === "application/octet-stream" ||
    mime === "application/jfif" ||
    mime === "image/jfif"
  ) {
    mime =
      forcedMimeFallback ||
      (absoluteUrl.endsWith(".png") ? "image/png" : "image/jpeg");
  }
  return resizeImageBufferToPrintDataUrl(buf, mime, maxWidth);
};

/** Small inlined PNG data URLs for fast print preview (webpack URL remains fallback). */
const fetchPrintLogoAsDataUrl = async () => {
  const bundledCandidates = [
    { url: resolveSameOriginUrl(bundledPrintLogo), mime: "image/png" },
    { url: resolveSameOriginUrl(bundledFallbackLogo), mime: "image/png" },
  ];
  for (const candidate of bundledCandidates) {
    try {
      const dataUrl = await fetchLogoUrlToPrintDataUrl(
        candidate.url,
        candidate.mime,
        PRINT_LOGO_MAX_WIDTH_PX
      );
      if (dataUrl && dataUrl.startsWith("data:image")) {
        return dataUrl;
      }
    } catch {
      /* try next */
    }
  }

  const base = getPublicAssetBaseUrl();
  if (!base) {
    return "";
  }
  const publicCandidates = [`${base}/alkhidmat-print-logo-new.png`, `${base}/alkhidmat-logo.png`];
  for (let i = 0; i < publicCandidates.length; i += 1) {
    const url = publicCandidates[i];
    try {
      const dataUrl = await fetchLogoUrlToPrintDataUrl(
        url,
        url.endsWith(".png") ? "image/png" : "image/jpeg",
        PRINT_LOGO_MAX_WIDTH_PX
      );
      if (dataUrl && dataUrl.startsWith("data:image")) {
        return dataUrl;
      }
    } catch {
      /* try next */
    }
  }
  return "";
};

const getFiftyYearsLogoPublicUrl = () => {
  const base = getPublicAssetBaseUrl();
  return base ? `${base}/${encodeURIComponent("50 years logo alkhidmat.png")}` : "";
};

const fetchFiftyYearsLogoAsDataUrl = async () => {
  const url = getFiftyYearsLogoPublicUrl();
  if (!url) {
    return "";
  }
  try {
    const dataUrl = await fetchLogoUrlToPrintDataUrl(
      url,
      "image/png",
      PRINT_FIFTY_YEARS_MAX_WIDTH_PX
    );
    return dataUrl && dataUrl.startsWith("data:image") ? dataUrl : "";
  } catch {
    return "";
  }
};

let cachedPrintLogoDataUrlPromise = null;
const getCachedPrintLogoDataUrl = () => {
  if (!cachedPrintLogoDataUrlPromise) {
    cachedPrintLogoDataUrlPromise = fetchPrintLogoAsDataUrl().catch(() => "");
  }
  return cachedPrintLogoDataUrlPromise;
};

let cachedFiftyYearsLogoDataUrlPromise = null;
const getCachedFiftyYearsLogoDataUrl = () => {
  if (!cachedFiftyYearsLogoDataUrlPromise) {
    cachedFiftyYearsLogoDataUrlPromise = fetchFiftyYearsLogoAsDataUrl().catch(() => "");
  }
  return cachedFiftyYearsLogoDataUrlPromise;
};

const getFastPrintImageDataUrls = async (logoPromise, fiftyYearsPromise) => {
  const [logoDataUrl, fiftyYearsDataUrl] = await Promise.all([
    getFastLogoDataUrl(logoPromise, PRINT_LOGO_MAX_WAIT_MS),
    getFastLogoDataUrl(fiftyYearsPromise, PRINT_LOGO_MAX_WAIT_MS),
  ]);
  return { logoDataUrl, fiftyYearsDataUrl };
};

const getFastLogoDataUrl = async (logoPromise, maxWaitMs = PRINT_LOGO_MAX_WAIT_MS) =>
  Promise.race([
    logoPromise.catch(() => ""),
    new Promise((resolve) => {
      window.setTimeout(() => resolve(""), maxWaitMs);
    }),
  ]);

const escapeQuotedAttr = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;");

/** Nested API blob first, then flat fields on the token/slip. */
const getReceiptPrintSources = (slip) => {
  const nested =
    slip?.print_slip && typeof slip.print_slip === "object" && !Array.isArray(slip.print_slip)
      ? slip.print_slip
      : slip?.printSlip && typeof slip.printSlip === "object" && !Array.isArray(slip.printSlip)
        ? slip.printSlip
        : null;
  return [nested, slip].filter((s) => s && typeof s === "object" && !Array.isArray(s));
};

const pickReceiptString = (sources, keys) => {
  const list = Array.isArray(keys) ? keys : [keys];
  for (const key of list) {
    for (const src of sources) {
      const v = src[key];
      if (v == null) continue;
      const str = String(v).trim();
      if (str) return str;
    }
  }
  return "";
};

const pickReceiptRecord = (sources, keys) => {
  const list = Array.isArray(keys) ? keys : [keys];
  for (const key of list) {
    for (const src of sources) {
      const v = src?.[key];
      if (v && typeof v === "object" && !Array.isArray(v)) return v;
    }
  }
  return null;
};

const pickReceiptStringArray = (sources, keys) => {
  const list = Array.isArray(keys) ? keys : [keys];
  for (const key of list) {
    for (const src of sources) {
      const v = src?.[key];
      if (!Array.isArray(v)) continue;
      const out = v.map((item) => String(item ?? "").trim()).filter(Boolean);
      if (out.length) return out;
    }
  }
  return [];
};

const buildAlkhidmatA4ReceiptHtml = (
  slip,
  logoDataUrlEmbedded = "",
  fiftyYearsDataUrlEmbedded = ""
) => {
  const printSources = getReceiptPrintSources(slip);
  const printLogoWebpackAbs =
    typeof window !== "undefined" ? resolveSameOriginUrl(bundledPrintLogo) : "";
  const printLogoPngWebpackAbs =
    typeof window !== "undefined" ? resolveSameOriginUrl(bundledFallbackLogo) : "";

  const dataPreferred =
    logoDataUrlEmbedded && String(logoDataUrlEmbedded).startsWith("data:image")
      ? logoDataUrlEmbedded
      : "";
  /** Always have a raster URL — print preview skips <img> if this was empty (no broken box). */
  const logoSrcPrimary =
    dataPreferred ||
    printLogoWebpackAbs ||
    printLogoPngWebpackAbs ||
    (typeof window !== "undefined"
      ? `${getPublicAssetBaseUrl()}/alkhidmat-print-logo-new.png`
      : "");

  const printBaseHref = `${getPublicAssetBaseUrl()}/`;
  const exitTime =
    String(slip.updated_at || "").trim() ||
    slip.data_entered_time ||
    slip.data_entered_at ||
    slip.token_generated_time ||
    slip.entry_time ||
    "—";
  const issuerName =
    String(slip.data_entered_by_name || "").trim() ||
    formatUserDisplay(slip.data_entered_by_username, slip.data_entered_by_name);
  const subLine = [slip.tannery_name && slip.tannery_name !== "-" ? slip.tannery_name : "", slip.eid_day]
    .filter(Boolean)
    .join(" — ");
  const subDisplay = subLine || "Modern Tannery";

  const totals = LIVESTOCK_FIELDS.reduce((sum, field) => sum + Number(slip[field.key] ?? 0), 0);
  const copyLabelsFromApi = pickReceiptStringArray(printSources, [
    "copy_labels",
    "copyLabels",
    "section_copy_labels",
    "sectionCopyLabels",
  ]);
  const originalCopyLabelFromApi = pickReceiptString(printSources, [
    "original_copy_label",
    "originalCopyLabel",
    "office_copy_label",
    "officeCopyLabel",
    "first_copy_label",
    "firstCopyLabel",
  ]);
  const duplicateCopyLabelFromApi = pickReceiptString(printSources, [
    "duplicate_copy_label",
    "duplicateCopyLabel",
    "receiver_copy_label",
    "receiverCopyLabel",
    "second_copy_label",
    "secondCopyLabel",
  ]);
  const secondCopyLabelFromApi = pickReceiptString(printSources, [
    "copy_label_second",
    "copyLabelSecond",
    "copy_label_2",
    "copyLabel2",
  ]);
  const sharedCopyLabelFromApi = pickReceiptString(printSources, [
    "copy_label",
    "copyLabel",
    "print_copy_label",
    "printCopyLabel",
  ]);
  const printCountFromApi = Number(
    pickReceiptString(printSources, ["print_count", "printCount"]) || slip.print_count || 0
  );
  const isReprint = Number.isFinite(printCountFromApi) && printCountFromApi > 1;
  const formatHideCount = (value) => {
    const parsed = Number(value ?? 0);
    if (!Number.isFinite(parsed)) {
      return "0";
    }
    return parsed.toLocaleString("en-US");
  };
  const firstSectionEndpointTag =
    originalCopyLabelFromApi ||
    copyLabelsFromApi[0] ||
    sharedCopyLabelFromApi ||
    (isReprint ? "Duplicate Copy" : "Original Copy");
  const secondSectionEndpointTag =
    duplicateCopyLabelFromApi ||
    copyLabelsFromApi[1] ||
    secondCopyLabelFromApi ||
    sharedCopyLabelFromApi ||
    "Duplicate Copy";
  const logoImgHtml = logoSrcPrimary
    ? `<img class="rc-logo-img" alt="" decoding="sync" referrerpolicy="same-origin"
          src="${escapeQuotedAttr(logoSrcPrimary)}"
          ${
            printLogoPngWebpackAbs && printLogoPngWebpackAbs !== logoSrcPrimary
              ? ` onerror='this.onerror=null;this.src=${JSON.stringify(printLogoPngWebpackAbs)};'`
              : ""
          } />`
    : "";

  const fiftyPublicUrl = typeof window !== "undefined" ? getFiftyYearsLogoPublicUrl() : "";
  const fiftyYearsSrc =
    fiftyYearsDataUrlEmbedded && String(fiftyYearsDataUrlEmbedded).startsWith("data:image")
      ? fiftyYearsDataUrlEmbedded
      : fiftyPublicUrl;
  const fiftyYearsImgHtml = fiftyYearsSrc
    ? `<img class="rc-50-img" alt="" decoding="sync" referrerpolicy="same-origin"
          src="${escapeQuotedAttr(fiftyYearsSrc)}" />`
    : "";

  const tableCells = LIVESTOCK_FIELDS.map((field) =>
    `<td>${escapeHtml(formatHideCount(slip[field.key] ?? 0))}</td>`
  ).join("");
  const tableHeaders = LIVESTOCK_FIELDS.map((field) => `<th>${escapeHtml(field.label)}</th>`).join("");

  const receiptHalf = (sectionTitle, endpointTag) => `
    <section class="receipt-copy">
      <header class="rc-head">
        <div class="rc-head-main">
          <h1 class="rc-main-title">RECEIPT FOR QURBANI HIDES</h1>
          <div class="rc-org">Alkhidmat Karachi</div>
          <div class="rc-sub">${escapeHtml(subDisplay)}</div>
          <div class="rc-sub-copy">${escapeHtml(endpointTag)}</div>
        </div>
        <div class="rc-copy-tag rc-copy-tag-head">${escapeHtml(sectionTitle)}</div>
        <div class="rc-head-divider" aria-hidden="true"></div>
        <div class="rc-head-right">
          ${fiftyYearsImgHtml}
        </div>
      </header>
      <div class="rc-solid-line"></div>

      <div class="rc-grid">
        <div class="rc-col">
          <div class="kv-row kv-row-token">
            <span class="kv-lab">TOKEN #</span>
            <span class="token-pill">${escapeHtml(slip.short_code)}</span>
          </div>
          <div class="kv-row"><span class="kv-lab">NAME</span><span class="kv-val">${escapeHtml(slip.name)}</span></div>
          <div class="kv-row"><span class="kv-lab">PHONE</span><span class="kv-val">${escapeHtml(
            slip.phone_number
          )}</span></div>
          <div class="kv-row"><span class="kv-lab">VEHICLE PLATE</span><span class="kv-val">${escapeHtml(
            slip.car_plate
          )}</span></div>
        </div>
        <div class="rc-col">
          <div class="kv-row"><span class="kv-lab">ISSUER</span><span class="kv-val">${escapeHtml(
            issuerName
          )}</span></div>
          <div class="kv-row"><span class="kv-lab">ELAQA</span><span class="kv-val">${escapeHtml(
            slip.elaqa
          )}</span></div>
          <div class="kv-row"><span class="kv-lab">DISTRICT</span><span class="kv-val">${escapeHtml(
            slip.zila
          )}</span></div>
          <div class="kv-row"><span class="kv-lab">EXIT TIME</span><span class="kv-val">${escapeHtml(
            exitTime
          )}</span></div>
        </div>
      </div>

      <div class="rc-summary-bar">
        <h2 class="rc-summary-title">HIDES SUMMARY</h2>
      </div>
      <table class="rc-table" cellspacing="0" cellpadding="0">
        <thead><tr>${tableHeaders}<th class="rc-th-total">Total</th></tr></thead>
        <tbody><tr>${tableCells}<td class="rc-td-total">${escapeHtml(formatHideCount(totals))}</td></tr></tbody>
      </table>

      <footer class="rc-foot">
        <div class="rc-foot-left">
          ${logoImgHtml ? `<div class="rc-foot-logo">${logoImgHtml}</div>` : ""}
          <div class="rc-foot-links">
            <div>https://cqurbani.alkhidmat.com/home/checkout</div>
            <div>www.alkhidmat.com</div>
          </div>
        </div>
        <div class="rc-foot-mid">
          <div class="rc-sig-line"></div>
          <div class="rc-sig-lab">SIGNATURE</div>
        </div>
        <div class="rc-foot-right">1/1</div>
      </footer>
    </section>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(slip.short_code || "Receipt")}</title>
${printBaseHref ? `<base href="${escapeHtml(printBaseHref)}" />` : ""}
<style>
  @page { size: A4 portrait; margin: 6mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    height: auto;
    max-height: none;
  }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #1a1a1a;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .a4-sheet {
    width: 100%;
    height: 285mm;
    max-height: 285mm;
    min-height: 0;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    background: #fff;
    overflow: hidden;
    page-break-after: avoid;
    page-break-inside: avoid;
  }
  .receipt-copy {
    flex: 0 0 50%;
    height: 50%;
    min-height: 0;
    max-height: 50%;
    display: flex;
    flex-direction: column;
    padding: 2.5mm 5mm 2mm;
    overflow: hidden;
  }
  .receipt-copy:not(:last-child) {
    padding-bottom: 5mm;
    border-bottom: 2px dashed #8b95a3;
  }
  .receipt-copy:last-child {
    padding-top: 5mm;
    border-bottom: none;
  }
  .rc-head {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 0;
    flex-shrink: 0;
  }
  .rc-logo-img {
    display: block;
    max-height: 44px;
    max-width: 180px;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: left center;
    border: none;
    margin: 0;
  }
  .rc-head-divider {
    align-self: stretch;
    width: 1px;
    min-height: 96px;
    background: #cfd4dc;
    flex-shrink: 0;
  }
  .rc-head-main {
    flex: 1 1 auto;
    min-width: 0;
    text-align: left;
    padding-left: 10px;
    padding-right: 8px;
    padding-top: 0;
  }
  .rc-head-right {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-start;
    gap: 3px;
    padding-left: 10px;
    padding-top: 1px;
    min-width: 0;
    max-width: 200px;
  }
  .rc-50-img {
    display: block;
    flex: 0 0 auto;
    max-height: 72px;
    max-width: 120px;
    width: auto;
    height: auto;
    object-fit: contain;
    object-position: right top;
    border: none;
    margin: 0;
  }
  .rc-main-title {
    margin: 0;
    font-family: Georgia, "Times New Roman", serif;
    font-weight: 400;
    font-size: 16pt;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7a8494;
    line-height: 1.15;
  }
  .rc-org {
    margin-top: 6px;
    font-family: Georgia, "Times New Roman", serif;
    font-weight: 700;
    font-size: 11.5pt;
    color: #000;
    line-height: 1.2;
  }
  .rc-sub {
    margin-top: 5px;
    font-family: Arial, Helvetica, sans-serif;
    font-weight: 400;
    font-size: 10.5pt;
    color: #000;
    line-height: 1.25;
  }
  .rc-sub-copy {
    margin-top: 2px;
    font-family: Arial, Helvetica, sans-serif;
    font-weight: 400;
    font-size: 10.5pt;
    color: #000;
    line-height: 1.25;
  }
  .rc-copy-tag {
    margin-top: 0;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9.5pt;
    font-weight: 800;
    letter-spacing: 0.04em;
    color: #1e3d5c;
    text-transform: none;
    text-align: right;
    white-space: nowrap;
  }
  .rc-copy-tag-head {
    margin-left: 8px;
    margin-right: 8px;
    margin-top: 2px;
    align-self: flex-start;
    text-align: right;
    white-space: nowrap;
  }
  .rc-solid-line {
    height: 2px;
    background: #dce1ea;
    margin: 6px 0 8px;
    flex-shrink: 0;
  }
  .rc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 18px;
    font-size: 10.5pt;
    flex: 0 0 auto;
  }
  .rc-col {
    min-width: 0;
  }
  .kv-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    padding: 9px 0;
    border-bottom: 1px dotted #bdbdbd;
  }
  .kv-lab {
    font-weight: 700;
    color: #444;
    letter-spacing: 0.02em;
    font-size: 10.5pt;
  }
  .kv-val {
    text-align: right;
    font-weight: 400;
    color: #111;
    word-break: break-word;
    font-size: 10.5pt;
  }
  .token-pill {
    display: inline-block;
    background: #1e3d5c;
    color: #fff;
    font-weight: 700;
    font-size: 10.5pt;
    padding: 4px 12px;
    border-radius: 999px;
    white-space: nowrap;
  }
  .rc-summary-bar {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    margin-top: 10px;
    margin-bottom: 6px;
    flex-shrink: 0;
  }
  .rc-summary-title {
    margin: 0;
    font-size: 8.5pt;
    font-weight: 800;
    letter-spacing: 0.14em;
    color: #8a93a3;
  }
  .rc-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 10.5pt;
    flex-shrink: 0;
    border: 1px solid #cfd6e2;
  }
  .rc-table thead th {
    background: #2c445e;
    color: #fff;
    font-weight: 700;
    padding: 8px 4px;
    text-align: center;
    border: 1px solid #243a52;
    font-size: 10pt;
  }
  .rc-table tbody td {
    background: #eef1f6;
    color: #222;
    text-align: center;
    padding: 10px 4px;
    font-weight: 600;
    border: 1px solid #d5dbe6;
    font-size: 10.5pt;
  }
  .rc-th-total,
  .rc-td-total {
    width: 13%;
  }
  .rc-td-total {
    background: #eef1f6;
    color: #222;
    font-size: 14pt;
    font-weight: 800;
    border: 3px solid #1a4063;
  }
  .rc-foot {
    margin-top: auto;
    display: grid;
    grid-template-columns: 1.15fr 1fr auto;
    align-items: end;
    gap: 6px;
    padding-top: 8px;
    flex-shrink: 0;
    font-size: 8pt;
    color: #555;
  }
  .rc-foot-left {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .rc-foot-logo .rc-logo-img {
    max-height: 28px;
    max-width: 140px;
  }
  .rc-foot-links div {
    line-height: 1.3;
    word-break: break-all;
  }
  .rc-foot-mid {
    text-align: center;
    padding: 0 8px;
  }
  .rc-sig-line {
    height: 1px;
    background: #333;
    max-width: 160px;
    margin: 0 auto 2px;
  }
  .rc-sig-lab {
    font-size: 7pt;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #333;
  }
  .rc-foot-right {
    text-align: right;
    font-weight: 700;
    color: #333;
    padding-bottom: 1px;
  }
  @media print {
    html, body {
      height: auto;
      margin: 0 !important;
      padding: 0 !important;
    }
    .a4-sheet {
      height: 285mm;
      max-height: 285mm;
    }
  }
  @media screen {
    body {
      background: #e7edf7;
      padding: 12px;
    }
    .a4-sheet {
      width: 210mm;
      height: 297mm;
      max-height: 297mm;
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
    }
  }
</style>
</head>
<body>
  <div class="a4-sheet">
    ${receiptHalf("Office Copy", firstSectionEndpointTag)}
    ${receiptHalf("illaqa/Zila Copy", secondSectionEndpointTag)}
  </div>
</body>
</html>`;
};

const extractUpdatedTokenPayload = (payload) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {};
  }

  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    if (
      payload.data.token &&
      typeof payload.data.token === "object" &&
      !Array.isArray(payload.data.token)
    ) {
      return payload.data.token;
    }
    return payload.data;
  }

  if (payload.token && typeof payload.token === "object" && !Array.isArray(payload.token)) {
    return payload.token;
  }

  return payload;
};

const waitForSingleImageForPrint = (img) =>
  new Promise((resolve) => {
    const finish = () => resolve();
    if (img.complete && img.naturalWidth > 0) {
      const decoded = img.decode?.();
      if (decoded && typeof decoded.then === "function") {
        decoded.catch(() => undefined).finally(finish);
      } else {
        finish();
      }
      return;
    }
    const onLoad = () => {
      img.removeEventListener("error", onError);
      const decoded = img.decode?.();
      if (decoded && typeof decoded.then === "function") {
        decoded.catch(() => undefined).finally(finish);
      } else {
        finish();
      }
    };
    const onError = () => {
      img.removeEventListener("load", onLoad);
      finish();
    };
    img.addEventListener("load", onLoad, { once: true });
    img.addEventListener("error", onError, { once: true });
  });

const waitForPrintDocumentImages = (doc, timeoutMs = 6000) => {
  const imgs = [...doc.getElementsByTagName("img")];
  if (imgs.length === 0) {
    return Promise.resolve();
  }
  return Promise.race([
    Promise.all(imgs.map((img) => waitForSingleImageForPrint(img))),
    new Promise((resolve) => {
      window.setTimeout(resolve, timeoutMs);
    }),
  ]);
};

/** Close the about:blank print tab after Print / Save as PDF / Cancel on the dialog. */
const closePrintWindowAfterDialog = (printWindow) => {
  if (!printWindow || printWindow.closed) {
    return;
  }

  let closed = false;
  const closeOnce = () => {
    if (closed || !printWindow || printWindow.closed) {
      return;
    }
    closed = true;
    try {
      printWindow.close();
    } catch {
      /* ignore */
    }
  };

  try {
    printWindow.addEventListener("afterprint", closeOnce, { once: true });
  } catch {
    /* ignore */
  }
  try {
    printWindow.document?.addEventListener("afterprint", closeOnce, { once: true });
  } catch {
    /* ignore */
  }

  try {
    const mediaQuery = printWindow.matchMedia?.("print");
    if (mediaQuery) {
      const onPrintMediaChange = (event) => {
        if (!event.matches) {
          window.setTimeout(closeOnce, 200);
        }
      };
      if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", onPrintMediaChange);
      } else if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(onPrintMediaChange);
      }
    }
  } catch {
    /* ignore */
  }
};

function TokensPage() {
  const navigate = useNavigate();
  const userRole = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY));
  const isDataEntry = userRole === "DATA_ENTRY" || userRole === "DATAENTRY";
  const isTanneryAdmin = userRole === "TANNERY_ADMIN";
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  /** PATCH /tokens/{id}/livestock — optional elaqa correction; zila is never changed here. */
  const canPatchLivestock = isDataEntry || isTanneryAdmin || isSuperAdmin;
  const canPatchElaqa = canPatchLivestock;
  const usesServerPrintLedger = isDataEntry || isTanneryAdmin;
  const isAllowedRole = canPatchLivestock;

  const [tokens, setTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [checkoutFilter, setCheckoutFilter] = useState("waiting");
  const [editingToken, setEditingToken] = useState(null);
  const [modalMode, setModalMode] = useState("checkout");
  const [isSaving, setIsSaving] = useState(false);
  const [livestockHistory, setLivestockHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [formData, setFormData] = useState({
    eid_day: "",
    elaqa: "",
    cow_count: "0",
    goat_count: "0",
    sheep_count: "0",
    camel_count: "0",
    buffalo_count: "0",
    comment: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [printingTokenId, setPrintingTokenId] = useState("");
  const livestockInitialElaqaRef = useRef("");
  const [elaqaOptions, setElaqaOptions] = useState([]);
  const [elaqaLoadError, setElaqaLoadError] = useState("");
  const [isElaqasLoading, setIsElaqasLoading] = useState(false);
  const [tanneryDefaultEidDayMaxLabel, setTanneryDefaultEidDayMaxLabel] = useState(null);
  const [tokenSearchInput, setTokenSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const submitEidDayOptions = useMemo(
    () => buildSubmitEidDayOptions(tanneryDefaultEidDayMaxLabel),
    [tanneryDefaultEidDayMaxLabel]
  );

  const livestockQuantityTotal = useMemo(() => sumLivestockCounts(formData), [formData]);

  const hasHideCountInput = livestockQuantityTotal > 0;

  const checkoutSubmitLabel = hasHideCountInput ? "Save & Print" : "Save";

  const parseResponsePayload = async (response) => {
    const responseText = await response.text();
    if (!responseText) {
      return null;
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      return responseText;
    }
  };

  const fetchTokens = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      setListError("");
      const response = await fetch(`${getBaseApiUrl()}/tokens`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(
          getFriendlyErrorMessage(
            response.status,
            payload,
            "Unable to load tokens right now."
          )
        );
      }

      const tokenList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      const normalizedTokens = tokenList.map((tokenItem) => normalizeToken(tokenItem));

      setTokens(normalizedTokens);
    } catch (error) {
      setListError(error.message || "Unable to load tokens right now.");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const searchTokens = useCallback(async () => {
    const query = normalizeTokenSearchQuery(tokenSearchInput);
    if (!query) {
      setSearchError(
        "Enter a search term (token code, name, phone, plate, vehicle, elaqa, zila, tannery, or user)."
      );
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsSearching(true);
      setSearchError("");
      setSuccessMessage("");
      const response = await fetch(
        `${getBaseApiUrl()}/tokens/search/${encodeURIComponent(query)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(
          getFriendlyErrorMessage(
            response.status,
            payload,
            "Unable to search tokens right now."
          )
        );
      }

      const tokenList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];

      const normalizedResults = tokenList.map((tokenItem) => normalizeToken(tokenItem));
      setSearchResults(normalizedResults);
      setHasSearched(true);

      setTokens((prev) => {
        const byId = new Map(prev.map((item) => [item.id, item]));
        normalizedResults.forEach((found) => {
          if (found.id) {
            byId.set(found.id, found);
          }
        });
        return Array.from(byId.values());
      });
    } catch (error) {
      setSearchResults([]);
      setHasSearched(true);
      setSearchError(error.message || "Unable to search tokens right now.");
    } finally {
      setIsSearching(false);
    }
  }, [navigate, tokenSearchInput]);

  const clearTokenSearch = () => {
    setTokenSearchInput("");
    setSearchResults([]);
    setSearchError("");
    setHasSearched(false);
  };

  const handleTokenSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void searchTokens();
    }
  };

  const syncTokenInLists = useCallback((updatedToken) => {
    if (!updatedToken?.id) {
      return;
    }
    setTokens((prev) =>
      prev.map((tokenItem) =>
        tokenItem.id === updatedToken.id ? { ...tokenItem, ...updatedToken } : tokenItem
      )
    );
    setSearchResults((prev) =>
      prev.map((tokenItem) =>
        tokenItem.id === updatedToken.id ? { ...tokenItem, ...updatedToken } : tokenItem
      )
    );
  }, []);

  useEffect(() => {
    if (!isAllowedRole) {
      return;
    }
    fetchTokens();
  }, [fetchTokens, isAllowedRole]);

  useEffect(() => {
    if (!isAllowedRole) {
      return;
    }
    // Warm compressed print images in background so first print opens faster.
    void getCachedPrintLogoDataUrl();
    void getCachedFiftyYearsLogoDataUrl();
  }, [isAllowedRole]);

  useEffect(() => {
    if (!isAllowedRole) {
      return;
    }
    if (userRole === "SUPER_ADMIN") {
      setTanneryDefaultEidDayMaxLabel(null);
      return;
    }
    if (!isDataEntry && !isTanneryAdmin) {
      setTanneryDefaultEidDayMaxLabel(null);
      return;
    }

    let cancelled = false;
    const loadTanneryDefaultEidDay = async () => {
      const authToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (!authToken) {
        return;
      }

      try {
        const response = await fetch(
          `${getBaseApiUrl()}/tannery-admin/settings/eid-day`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              Accept: "application/json",
            },
          }
        );
        const payload = await parseResponsePayload(response);
        if (cancelled) {
          return;
        }
        if (response.ok) {
          const day = extractEidDayFromSettingsPayload(payload);
          if (day) {
            setTanneryDefaultEidDayMaxLabel(day);
            localStorage.setItem(TANNERY_DEFAULT_EID_DAY_STORAGE_KEY, day);
            return;
          }
        }
        const cached = localStorage.getItem(TANNERY_DEFAULT_EID_DAY_STORAGE_KEY);
        if (cached?.trim()) {
          setTanneryDefaultEidDayMaxLabel(cached.trim());
          return;
        }
        setTanneryDefaultEidDayMaxLabel(null);
      } catch {
        if (!cancelled) {
          const cached = localStorage.getItem(TANNERY_DEFAULT_EID_DAY_STORAGE_KEY);
          setTanneryDefaultEidDayMaxLabel(cached?.trim() || null);
        }
      }
    };

    void loadTanneryDefaultEidDay();
    return () => {
      cancelled = true;
    };
  }, [isAllowedRole, isDataEntry, isTanneryAdmin, userRole]);

  useEffect(() => {
    if (!editingToken || modalMode !== "checkout") {
      return;
    }
    const allowed = new Set(submitEidDayOptions);
    setFormData((prev) => {
      if (prev.eid_day && !allowed.has(prev.eid_day)) {
        return { ...prev, eid_day: "" };
      }
      return prev;
    });
  }, [submitEidDayOptions, editingToken, modalMode]);

  useEffect(() => {
    const fetchLivestockHistory = async () => {
      if (!editingToken?.id) {
        setLivestockHistory([]);
        setHistoryError("");
        setIsHistoryLoading(false);
        return;
      }

      const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (!token) {
        setHistoryError("Your session has expired. Please login again.");
        setLivestockHistory([]);
        navigate("/", { replace: true });
        return;
      }

      try {
        setIsHistoryLoading(true);
        setHistoryError("");
        const response = await fetch(
          `${getBaseApiUrl()}/tokens/${editingToken.id}/livestock-history`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        const payload = await parseResponsePayload(response);
        if (!response.ok) {
          throw new Error(
            getFriendlyErrorMessage(
              response.status,
              payload,
              "Unable to load token livestock history right now."
            )
          );
        }

        const historyList = Array.isArray(payload?.data) ? payload.data : [];
        setLivestockHistory(normalizeLivestockHistoryList(historyList));
      } catch (error) {
        setLivestockHistory([]);
        setHistoryError(error.message || "Unable to load token livestock history right now.");
      } finally {
        setIsHistoryLoading(false);
      }
    };

    fetchLivestockHistory();
  }, [editingToken, navigate]);

  useEffect(() => {
    if (!editingToken?.id || modalMode !== "checkout" || !canPatchElaqa) {
      setElaqaOptions([]);
      setElaqaLoadError("");
      setIsElaqasLoading(false);
      return;
    }

    const zilaName = String(editingToken.zila || "").trim();
    const zilaId = editingToken.zila_id ?? editingToken.zilaId ?? "";
    if ((!zilaName || zilaName === "-") && !zilaId) {
      setElaqaOptions([]);
      return;
    }

    let cancelled = false;
    const loadElaqas = async () => {
      const authToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (!authToken) {
        return;
      }

      const districtHeaders = {
        Authorization: `Bearer ${authToken}`,
        Accept: "application/json",
      };

      const mergeWithCurrentElaqa = (names) => {
        const merged = Array.from(new Set(names));
        const currentElaqa = normalizeElaqaForForm(editingToken.elaqa);
        if (currentElaqa && !merged.includes(currentElaqa)) {
          merged.push(currentElaqa);
        }
        merged.sort((a, b) => a.localeCompare(b));
        return merged;
      };

      try {
        setIsElaqasLoading(true);
        setElaqaLoadError("");

        let names = [];

        const perZilaResponse = await fetch(buildDistrictZilaElaqasUrl(zilaName, zilaId), {
          headers: districtHeaders,
        });
        const perZilaPayload = await parseResponsePayload(perZilaResponse);
        if (!perZilaResponse.ok) {
          throw new Error(
            getFriendlyErrorMessage(
              perZilaResponse.status,
              perZilaPayload,
              "Unable to load elaqas for this zila."
            )
          );
        }
        names = extractElaqaNamesFromApiPayload(perZilaPayload);

        if (names.length === 0) {
          const allZilasResponse = await fetch(`${getBaseApiUrl()}/districts/zilas`, {
            headers: districtHeaders,
          });
          const allZilasPayload = await parseResponsePayload(allZilasResponse);
          if (allZilasResponse.ok && !isZilaNameOnlyListPayload(allZilasPayload)) {
            const zilaRecords = extractZilaRecordsFromApiPayload(allZilasPayload);
            const matchedZila = findZilaRecord(zilaRecords, zilaName, zilaId);
            if (matchedZila) {
              names = extractElaqasFromZilaRecord(matchedZila);
            }
          }
        }

        if (!cancelled) {
          setElaqaOptions(mergeWithCurrentElaqa(names));
        }
      } catch (error) {
        if (!cancelled) {
          setElaqaOptions([]);
          setElaqaLoadError(error.message || "Unable to load elaqas for this zila.");
        }
      } finally {
        if (!cancelled) {
          setIsElaqasLoading(false);
        }
      }
    };

    void loadElaqas();
    return () => {
      cancelled = true;
    };
  }, [
    editingToken?.id,
    editingToken?.zila,
    editingToken?.zila_id,
    editingToken?.elaqa,
    modalMode,
    canPatchElaqa,
  ]);

  const elaqaSelectOptions = useMemo(() => {
    const merged = new Set(elaqaOptions);
    if (editingToken) {
      const fromToken = normalizeElaqaForForm(editingToken.elaqa);
      if (fromToken) {
        merged.add(fromToken);
      }
    }
    const sorted = Array.from(merged).sort((a, b) => a.localeCompare(b));
    const opts = sorted.map((name) => ({ value: name, label: name }));
    if (editingToken && isCentralOfficeZila(editingToken.zila)) {
      return [{ value: "", label: "(None)" }, ...opts];
    }
    return opts;
  }, [elaqaOptions, editingToken]);

  const openEditModal = (token) => {
    setEditingToken(token);
    setModalMode("checkout");
    setFieldErrors({});
    setListError("");
    setSuccessMessage("");
    const initialElaqa = normalizeElaqaForForm(token.elaqa);
    livestockInitialElaqaRef.current = initialElaqa;
    setFormData({
      eid_day: token.eid_day || "",
      elaqa: initialElaqa,
      cow_count: String(token.cow_count ?? 0),
      goat_count: String(token.goat_count ?? 0),
      sheep_count: String(token.sheep_count ?? 0),
      camel_count: String(token.camel_count ?? 0),
      buffalo_count: String(token.buffalo_count ?? 0),
      comment: token.comment || "",
    });
  };

  const openViewModal = (token) => {
    setEditingToken(token);
    setModalMode("view");
    setFieldErrors({});
    setListError("");
  };

  const closeEditModal = () => {
    if (isSaving) {
      return;
    }
    setEditingToken(null);
    setModalMode("checkout");
    setFieldErrors({});
    setLivestockHistory([]);
    setHistoryError("");
    setIsHistoryLoading(false);
    setElaqaOptions([]);
    setElaqaLoadError("");
    setIsElaqasLoading(false);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    if (LIVESTOCK_FIELD_KEYS.has(name) && !/^\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    setListError("");
  };

  const handleQuantityKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      return;
    }
    if (["-", "+", "e", "E", "."].includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleLivestockFormKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }
    const target = event.target;
    if (target instanceof HTMLButtonElement && target.type === "submit") {
      return;
    }
    event.preventDefault();
  };

  const handleQuantityPaste = (event) => {
    const pastedText = event.clipboardData.getData("text");
    if (!/^\d+$/.test(pastedText.trim())) {
      event.preventDefault();
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.eid_day.trim()) {
      errors.eid_day = "Please select Eid day.";
    } else if (!submitEidDayOptions.includes(formData.eid_day)) {
      errors.eid_day = "This Eid day is not available for your tannery's current default.";
    }

    LIVESTOCK_FIELDS.forEach((field) => {
      const rawValue = formData[field.key];
      const parsedValue = Number(rawValue);
      if (rawValue === "" || Number.isNaN(parsedValue)) {
        errors[field.key] = `Please enter ${field.label.toLowerCase()} quantity.`;
        return;
      }
      if (!Number.isInteger(parsedValue)) {
        errors[field.key] = `${field.label} quantity must be a whole number.`;
        return;
      }
      if (parsedValue < 0) {
        errors[field.key] = `${field.label} quantity cannot be negative.`;
      }
    });

    if (editingToken?.checked_out && !formData.comment.trim()) {
      errors.comment = "Comment is required.";
    }

    if (canPatchElaqa && editingToken && !elaqaLoadError) {
      const elaqaTrim = String(formData.elaqa ?? "").trim();
      if (!isCentralOfficeZila(editingToken.zila)) {
        if (elaqaTrim.length === 0) {
          errors.elaqa = "Please select an elaqa for this zila.";
        } else if (elaqaTrim.length < 2) {
          errors.elaqa = "Elaqa must be at least 2 characters for this zila.";
        }
      }
    }

    return errors;
  };

  const printTokenSlip = async (tokenRow, { preOpenedWindow = null } = {}) => {
    if (!tokenRow.checked_out) {
      setListError(
        getPrintFriendlyErrorMessage(
          400,
          { detail: "This token cannot be printed until checkout is completed." },
          "This token cannot be printed until checkout is completed."
        )
      );
      return false;
    }

    const authToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!authToken) {
      setListError("Your session has expired. Please login again.");
      navigate("/", { replace: true });
      return false;
    }

    const printWindow =
      preOpenedWindow || window.open("", "_blank", "width=900,height=650");
    if (!printWindow) {
      setListError("Unable to open print preview. Please allow popups.");
      return false;
    }

    try {
      setListError("");
      setPrintingTokenId(tokenRow.id);
      const logoDataUrlPromise = getCachedPrintLogoDataUrl();
      const fiftyYearsDataUrlPromise = getCachedFiftyYearsLogoDataUrl();

      if (!usesServerPrintLedger) {
        const { logoDataUrl, fiftyYearsDataUrl } = await getFastPrintImageDataUrls(
          logoDataUrlPromise,
          fiftyYearsDataUrlPromise
        );
        await openPrintPreviewFromSlip(
          printWindow,
          { ...normalizeToken(tokenRow), print_count: "—" },
          logoDataUrl,
          fiftyYearsDataUrl
        );
        return true;
      }

      const [response, printImages] = await Promise.all([
        fetch(`${getBaseApiUrl()}/tokens/${encodeURIComponent(tokenRow.id)}/print`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
          },
        }),
        getFastPrintImageDataUrls(logoDataUrlPromise, fiftyYearsDataUrlPromise),
      ]);
      const { logoDataUrl, fiftyYearsDataUrl } = printImages;

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        printWindow.close();
        throw new Error(
          getPrintFriendlyErrorMessage(
            response.status,
            payload,
            "Unable to record print and prepare slip."
          )
        );
      }

      const printPayload = extractUpdatedTokenPayload(payload);
      const slip = { ...tokenRow, ...printPayload };

      await openPrintPreviewFromSlip(printWindow, slip, logoDataUrl, fiftyYearsDataUrl);

      const normalizedFresh = normalizeToken({ ...tokenRow, ...printPayload });
      syncTokenInLists(normalizedFresh);
      return true;
    } catch (error) {
      if (printWindow && !printWindow.closed) {
        printWindow.close();
      }
      setListError(error.message || "Unable to print this token.");
      return false;
    } finally {
      setPrintingTokenId("");
    }
  };

  const handleSaveLivestock = async (event) => {
    event.preventDefault();
    if (!editingToken) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    const shouldPrintAfterSave = hasHideCountInput;

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      setListError("Your session has expired. Please login again.");
      navigate("/", { replace: true });
      return;
    }

    let printWindow = null;
    if (shouldPrintAfterSave) {
      printWindow = window.open("", "_blank", "width=900,height=650");
      if (!printWindow) {
        setListError("Unable to open print preview. Please allow popups for Save & Print.");
        return;
      }
    }

    try {
      setIsSaving(true);
      setListError("");
      setSuccessMessage("");

      const requestBody = {
        eid_day: formData.eid_day,
        cow_count: Math.max(0, parseInt(formData.cow_count || "0", 10)),
        goat_count: Math.max(0, parseInt(formData.goat_count || "0", 10)),
        camel_count: Math.max(0, parseInt(formData.camel_count || "0", 10)),
        sheep_count: Math.max(0, parseInt(formData.sheep_count || "0", 10)),
        buffalo_count: Math.max(0, parseInt(formData.buffalo_count || "0", 10)),
        comment: editingToken.checked_out ? formData.comment.trim() : "",
      };

      if (canPatchElaqa) {
        const nextElaqa = String(formData.elaqa ?? "").trim();
        const initialElaqa = livestockInitialElaqaRef.current;
        const isFirstLivestockEntry = !editingToken.checked_out;
        if (isFirstLivestockEntry || nextElaqa !== initialElaqa) {
          requestBody.elaqa = nextElaqa === "" ? "" : nextElaqa;
        }
      }

      const response = await fetch(
        `${getBaseApiUrl()}/tokens/${editingToken.id}/livestock`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(
            getFriendlyErrorMessage(
            response.status,
            payload,
            "Unable to save livestock data right now."
          )
        );
      }

      const updatedTokenPayload = extractUpdatedTokenPayload(payload);
      const updatedToken = normalizeToken({
        ...editingToken,
        ...updatedTokenPayload,
        ...requestBody,
        update_check:
          updatedTokenPayload.update_check ?? editingToken.update_check ?? "NotUpdated",
      });
      const savedToken = { ...updatedToken, checked_out: true };
      syncTokenInLists(savedToken);
      setSuccessMessage(`Token ${updatedToken.short_code} updated successfully.`);

      if (shouldPrintAfterSave) {
        await printTokenSlip(savedToken, { preOpenedWindow: printWindow });
        printWindow = null;
      } else if (printWindow && !printWindow.closed) {
        printWindow.close();
      }

      setEditingToken(null);
      setModalMode("checkout");
      setFieldErrors({});
    } catch (error) {
      if (printWindow && !printWindow.closed) {
        printWindow.close();
      }
      setListError(error.message || "Unable to save livestock data right now.");
    } finally {
      setIsSaving(false);
    }
  };

  const openPrintPreviewFromSlip = async (
    printWindow,
    slip,
    logoDataUrl = "",
    fiftyYearsDataUrl = ""
  ) => {
    const html = buildAlkhidmatA4ReceiptHtml(slip, logoDataUrl, fiftyYearsDataUrl);
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    const logoInlined = String(logoDataUrl || "").startsWith("data:image");
    const fiftyInlined =
      !fiftyYearsDataUrl || String(fiftyYearsDataUrl).startsWith("data:image");
    const waitMs = logoInlined && fiftyInlined ? 40 : PRINT_IMAGE_WAIT_TIMEOUT_MS;

    try {
      await waitForPrintDocumentImages(printWindow.document, waitMs);
    } catch {
      /* still open print dialog */
    }
    await new Promise((resolve) => {
      window.setTimeout(resolve, 60);
    });
    closePrintWindowAfterDialog(printWindow);
    printWindow.focus();
    printWindow.print();
    if (!printWindow.closed) {
      printWindow.close();
    }
  };

  const printToken = async (tokenRow) => {
    await printTokenSlip(tokenRow);
  };

  const tokenCountLabel = useMemo(() => `${tokens.length} token(s)`, [tokens.length]);
  const filteredTokens = useMemo(() => {
    if (checkoutFilter === "checked_out") {
      return tokens.filter((token) => token.checked_out);
    }
    if (checkoutFilter === "waiting") {
      return tokens.filter((token) => !token.checked_out);
    }
    return tokens;
  }, [checkoutFilter, tokens]);
  const filteredTokenCountLabel = useMemo(
    () => `${filteredTokens.length} token(s) shown`,
    [filteredTokens.length]
  );
  const tokenKpis = useMemo(
    () => [
      { label: "Total Tokens", value: tokens.length },
      { label: "Waiting Tokens", value: tokens.filter((token) => !token.checked_out).length },
      { label: "Checked Out Tokens", value: tokens.filter((token) => token.checked_out).length },
    ],
    [tokens]
  );
  const readonlyTokenDetails = useMemo(() => {
    if (!editingToken) {
      return [];
    }

    const details = [
      { label: "Token Code", value: editingToken.short_code },
      { label: "Tannery Name", value: editingToken.tannery_name },
      { label: "Name", value: editingToken.name },
      { label: "Phone Number", value: editingToken.phone_number },
      { label: "Eid Day", value: editingToken.eid_day || "-" },
      { label: "Vehicle Type", value: editingToken.vehicle_type },
      { label: "Car Plate", value: editingToken.car_plate },
      { label: "Elaqa", value: editingToken.elaqa },
      { label: "Zila", value: editingToken.zila },
      { label: "Status", value: editingToken.status },
      { label: "Token Generated Time", value: editingToken.token_generated_at || "-" },
      {
        label: "Token Generated By",
        value: formatUserDisplay(
          editingToken.token_generated_by_username,
          editingToken.token_generated_by_name
        ),
      },
    ];

    if (editingToken.checked_out || String(editingToken.data_entered_at || "").trim()) {
      details.push({
        label: "Data Entered Time",
        value: editingToken.data_entered_at || "-",
      });
      details.push({
        label: "Data Entered By",
        value: formatUserDisplay(
          editingToken.data_entered_by_username,
          editingToken.data_entered_by_name
        ),
      });
    }

    return details;
  }, [editingToken]);
  const readonlyLivestockDetails = useMemo(() => {
    if (!editingToken) {
      return [];
    }

    return [
      { label: "Eid Day", value: editingToken.eid_day || "-" },
      ...LIVESTOCK_FIELDS.map((field) => ({
        label: field.label,
        value: editingToken[field.key] ?? 0,
      })),
      { label: "Total", value: sumLivestockCounts(editingToken) },
    ];
  }, [editingToken]);

  const renderTokenActions = (token) => (
    <td className="token-actions-cell">
      {!token.checked_out ? (
        <button
          type="button"
          className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
          onClick={() => openEditModal(token)}
          aria-label={`Enter hides details for token ${token.short_code}`}
        >
          Enter Hides
        </button>
      ) : (
        <>
          <span
            className="restriction-toggle-btn checkout-complete-btn"
            aria-label={`Token ${token.short_code} checked out`}
          >
            ✓ Checked Out
          </span>
          <button
            type="button"
            className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
            onClick={() => openEditModal(token)}
            aria-label={`Edit checked out token ${token.short_code}`}
          >
            Edit
          </button>
        </>
      )}
      <button
        type="button"
        className="icon-action-btn"
        onClick={() => openViewModal(token)}
        aria-label={`View token ${token.short_code}`}
        title="View"
      >
        👁
      </button>
      <button
        type="button"
        className="icon-action-btn"
        onClick={() => printToken(token)}
        disabled={!token.checked_out || (printingTokenId && printingTokenId === token.id)}
        aria-label={`Print token ${token.short_code}`}
        title={
          token.checked_out
            ? printingTokenId === token.id
              ? "Preparing print…"
              : "Print"
            : "Print available after checkout"
        }
      >
        🖨
      </button>
    </td>
  );

  const renderTokenTableBody = (tokenRows) =>
    tokenRows.map((token) => (
      <tr key={token.id}>
        <td>{token.short_code}</td>
        <td>{token.tannery_name}</td>
        <td>{token.name}</td>
        <td>{token.phone_number}</td>
        <td>{token.eid_day || "-"}</td>
        {renderTokenActions(token)}
      </tr>
    ));

  if (!isAllowedRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card">
      <div className="dashboard-users-list-header">
        <h2>Tokens</h2>
        <PrimaryButton onClick={fetchTokens}>
          {isLoading ? "Refreshing..." : "Refresh"}
        </PrimaryButton>
      </div>
      <p className="dashboard-card-subtitle">
        View generated tokens for your tannery, enter hides details, view details, and print.
      </p>

      <div className="dashboard-stats-grid">
        {tokenKpis.map((kpiItem) => (
          <article key={kpiItem.label} className="dashboard-stat-card">
            <p className="dashboard-stat-title">{kpiItem.label}</p>
            <p className="dashboard-stat-value">{kpiItem.value}</p>
          </article>
        ))}
      </div>

      <div className="token-search-toolbar" role="search">
        <label htmlFor="token-search-input" className="score-filter-label token-search-label">
          Search tokens
        </label>
        <input
          id="token-search-input"
          type="search"
          className="score-filter-select token-search-input"
          value={tokenSearchInput}
          onChange={(event) => setTokenSearchInput(event.target.value)}
          onKeyDown={handleTokenSearchKeyDown}
          placeholder="Token, name, phone, plate, zila, tannery, user…"
          disabled={isSearching}
          autoComplete="off"
          aria-describedby="token-search-hint"
        />
        <PrimaryButton type="button" onClick={() => void searchTokens()} disabled={isSearching}>
          {isSearching ? "Searching..." : "Search"}
        </PrimaryButton>
        {hasSearched ? (
          <button type="button" className="hide-breakdown-refresh" onClick={clearTokenSearch} disabled={isSearching}>
            Clear
          </button>
        ) : null}
      </div>
      <p id="token-search-hint" className="token-search-hint">
        Matches token code (with or without KH- prefix), name, phone, car plate, vehicle type, elaqa, zila,
        tannery name, and token generated or data entered by (username or name). Partial search is supported.
      </p>

      {searchError ? (
        <p className="dashboard-error-message" role="alert">
          {searchError}
        </p>
      ) : null}

      {hasSearched ? (
        <section className="token-search-results" aria-label="Token search results">
          <div className="dashboard-users-list-header">
            <h3>Search results</h3>
            <p className="token-list-count">{searchResults.length} token(s) found</p>
          </div>
          {isSearching ? <p>Searching tokens...</p> : null}
          {!isSearching && searchResults.length === 0 ? (
            <p>No tokens found for &ldquo;{normalizeTokenSearchQuery(tokenSearchInput)}&rdquo;.</p>
          ) : null}
          {!isSearching && searchResults.length > 0 ? (
            <div className="dashboard-users-table-wrapper">
              <table className="dashboard-users-table">
                <thead>
                  <tr>
                    <th scope="col">Token</th>
                    <th scope="col">Tannery Name</th>
                    <th scope="col">Name</th>
                    <th scope="col">Phone</th>
                    <th scope="col">Eid Day</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>{renderTokenTableBody(searchResults)}</tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      <p className="token-list-count">
        {tokenCountLabel} • {filteredTokenCountLabel}
      </p>
      <div className="score-day-toggle-row" role="group" aria-label="Filter tokens by checkout status">
        {CHECKOUT_FILTER_OPTIONS.map((filterOption) => (
          <button
            key={filterOption.value}
            type="button"
            className={`score-day-toggle-btn ${
              checkoutFilter === filterOption.value ? "score-day-toggle-btn-active" : ""
            }`}
            onClick={() => setCheckoutFilter(filterOption.value)}
            disabled={isLoading}
          >
            {filterOption.label}
          </button>
        ))}
      </div>

      {successMessage ? (
        <p className="dashboard-success-message" role="status">
          {successMessage}
        </p>
      ) : null}
      {listError ? (
        <p className="dashboard-error-message" role="alert">
          {listError}
        </p>
      ) : null}

      {isLoading ? <p>Loading tokens...</p> : null}

      {!isLoading && filteredTokens.length === 0 ? (
        <p>No tokens found for the selected checkout filter.</p>
      ) : null}

      {!isLoading && filteredTokens.length > 0 ? (
        <div className="dashboard-users-table-wrapper">
          <table className="dashboard-users-table">
            <thead>
              <tr>
                <th scope="col">Token</th>
                <th scope="col">Tannery Name</th>
                <th scope="col">Name</th>
                <th scope="col">Phone</th>
                <th scope="col">Eid Day</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>{renderTokenTableBody(filteredTokens)}</tbody>
          </table>
        </div>
      ) : null}

      {editingToken ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="token-hides-livestock-title"
        >
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id="token-hides-livestock-title">
                Token {editingToken.short_code}
                {editingToken.is_updated ? (
                  <span
                    className="token-modal-correction-badge"
                    title="Second or later livestock log — counts were corrected after the first entry"
                  >
                    Updated
                  </span>
                ) : null}
              </h2>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeEditModal}
                aria-label="Close token hides livestock form"
              >
                x
              </button>
            </div>
            <div className="modal-form">
              <section className="token-readonly-details" aria-label="Token details">
                <h3>Token Details</h3>
                <div className="token-readonly-grid">
                  {readonlyTokenDetails
                    .filter(
                      (detail) =>
                        !(
                          canPatchElaqa &&
                          modalMode === "checkout" &&
                          detail.label === "Elaqa"
                        )
                    )
                    .map((detail) => (
                      <div key={detail.label} className="token-readonly-item">
                        <p className="token-readonly-label">{detail.label}</p>
                        <p className="token-readonly-value">{detail.value}</p>
                      </div>
                    ))}
                  {canPatchElaqa && modalMode === "checkout" ? (
                    <div className="token-readonly-item token-readonly-item-elaqa">
                      {elaqaLoadError ? (
                        <p className="dashboard-error-message" role="alert">
                          {elaqaLoadError}
                        </p>
                      ) : null}
                      <FormSelect
                        id="elaqa"
                        name="elaqa"
                        label="Elaqa"
                        value={formData.elaqa}
                        onChange={handleFieldChange}
                        options={elaqaSelectOptions}
                        placeholder={isElaqasLoading ? "Loading elaqas..." : "Select elaqa"}
                        error={fieldErrors.elaqa}
                        required={!isCentralOfficeZila(editingToken?.zila)}
                        disabled={isElaqasLoading}
                      />
                      <p className="token-readonly-elaqa-hint">
                        List is for zila {editingToken?.zila || "—"}. Zila is not changed on save.
                      </p>
                    </div>
                  ) : null}
                </div>
              </section>

              {modalMode === "view" ? (
                <section className="token-readonly-details" aria-label="Hides details">
                  <h3>Hides Quantity</h3>
                  <div className="token-readonly-grid">
                    {readonlyLivestockDetails.map((detail) => (
                      <div key={detail.label} className="token-readonly-item">
                        <p className="token-readonly-label">{detail.label}</p>
                        <p className="token-readonly-value">{detail.value}</p>
                      </div>
                    ))}
                  </div>
                </section>
              ) : (
                <form
                  onSubmit={handleSaveLivestock}
                  onKeyDown={handleLivestockFormKeyDown}
                  noValidate
                >
                  <h3 className="token-quantity-title">Submit Quantity</h3>
                  <FormSelect
                    id="eid_day"
                    name="eid_day"
                    label="Eid Day"
                    value={formData.eid_day}
                    onChange={handleFieldChange}
                    options={submitEidDayOptions}
                    placeholder="Select Eid Day"
                    error={fieldErrors.eid_day}
                    required
                  />
                  {canPatchElaqa ? (
                    <>
                      <p className="token-livestock-counts-hint">
                        Set elaqa in Token Details above, then enter livestock quantities below.
                      </p>
                      <h4 className="token-livestock-subheading">Livestock quantities</h4>
                    </>
                  ) : null}
                  {LIVESTOCK_FIELDS.map((field) => (
                    <FormInput
                      key={field.key}
                      id={field.key}
                      name={field.key}
                      label={`${field.label} Quantity`}
                      type="number"
                      value={formData[field.key]}
                      onChange={handleFieldChange}
                      placeholder={`Enter ${field.label.toLowerCase()} quantity`}
                      error={fieldErrors[field.key]}
                      min="0"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      onKeyDown={handleQuantityKeyDown}
                      onPaste={handleQuantityPaste}
                      required
                    />
                  ))}
                  <FormInput
                    id="livestock_quantity_total"
                    name="livestock_quantity_total"
                    label="Total Quantity"
                    type="text"
                    value={String(livestockQuantityTotal)}
                    onChange={() => {}}
                    readOnly
                    tabIndex={-1}
                    className="token-livestock-total-input"
                    aria-readonly="true"
                  />
                  {editingToken?.checked_out ? (
                    <FormInput
                      id="comment"
                      name="comment"
                      label="Comment"
                      type="text"
                      value={formData.comment}
                      onChange={handleFieldChange}
                      placeholder="Add comment (required)"
                      error={fieldErrors.comment}
                      required
                    />
                  ) : null}
                  <div className="modal-footer">
                    <PrimaryButton type="button" onClick={closeEditModal}>
                      Cancel
                    </PrimaryButton>
                    <PrimaryButton type="submit">
                      {isSaving
                        ? "Saving..."
                        : editingToken?.checked_out && !hasHideCountInput
                          ? "Save Changes"
                          : checkoutSubmitLabel}
                    </PrimaryButton>
                  </div>
                </form>
              )}

              <section className="token-readonly-details" aria-label="Hides history">
                <h3>Hides History</h3>
                {isHistoryLoading ? <p>Loading history...</p> : null}
                {!isHistoryLoading && historyError ? <p>{historyError}</p> : null}
                {!isHistoryLoading && !historyError && livestockHistory.length === 0 ? (
                  <p>No history found.</p>
                ) : null}
                {!isHistoryLoading && !historyError && livestockHistory.length > 0 ? (
                  <div className="token-readonly-grid">
                    {livestockHistory.map((historyItem, index) => (
                      <div
                        key={
                          historyItem.id !== undefined && historyItem.id !== null
                            ? String(historyItem.id)
                            : `${historyItem.updated_at || "history"}-${index}`
                        }
                        className={`token-readonly-item token-history-entry-card${
                          historyItem.is_initial_entry ? "" : " token-history-entry-card--correction"
                        }`}
                      >
                        <p className="token-history-entry-type" aria-label="Entry type">
                          <span
                            className={
                              historyItem.is_initial_entry
                                ? "token-history-entry-type-pill token-history-entry-type-pill--initial"
                                : "token-history-entry-type-pill token-history-entry-type-pill--correction"
                            }
                          >
                            {historyItem.is_initial_entry ? "First entry" : "Correction"}
                          </span>
                        </p>
                        <p className="token-readonly-label">Updated At</p>
                        <p className="token-readonly-value">{historyItem.updated_at || "-"}</p>
                        <p className="token-readonly-label">Updated By Username</p>
                        <p className="token-readonly-value">
                          {historyItem.updated_by_username || "-"}
                        </p>
                        <p className="token-readonly-label">Updated By Name</p>
                        <p className="token-readonly-value">{historyItem.updated_by_name || "-"}</p>
                        <p className="token-readonly-label">Comment</p>
                        <p className="token-readonly-value">{historyItem.comment || "-"}</p>
                        <p className="token-readonly-label">Eid Day</p>
                        <p className="token-readonly-value">
                          {historyItem.previous_eid_day || "-"} → {historyItem.updated_eid_day || "-"}
                        </p>
                        {LIVESTOCK_FIELDS.map((field) => (
                          <div key={field.key}>
                            <p className="token-readonly-label">{field.label}</p>
                            <p className="token-readonly-value">
                              {historyItem[`previous_${field.key}`] ?? 0} →{" "}
                              {historyItem[`updated_${field.key}`] ?? 0}
                            </p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            </div>
            {modalMode === "view" ? (
              <div className="modal-footer">
                <PrimaryButton type="button" onClick={closeEditModal}>
                  Close
                </PrimaryButton>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default TokensPage;
